import { Stack, Typography } from '@mui/material';
import type { Account } from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { CloudProviderIndicator } from '@/components/cloudProviderIndicator';
import { PanelWrapper } from '@/components/panels/panelWrapper';
import { useDAL } from '@/contexts/useDAL';

import { RestoreAccountConfigCustomTags } from '../restoreAccountConfigCustomTags';

interface RestoreServerTagsPanelProps {
  account: Account;
  onAbort: () => void;
  onUpdate: () => Promise<void>;
}

export function RestoreServerTagsPanel(props: RestoreServerTagsPanelProps) {
  const dal = useDAL();
  const [originalTags, setOriginalTags] = useState<Record<string, string>>({});
  const [updatedTags, setUpdatedTags] = useState<Record<string, string>>({});
  const { data: body, isLoading } = dal.cloudAccounts.restore.tags.get(
    props.account.id
  );

  useEffect(() => {
    if (body) {
      const parsed = body.reduce<Record<string, string>>((acc, tag) => {
        acc[tag.key] = tag.value || '';
        return acc;
      }, {});
      setOriginalTags(parsed);
      setUpdatedTags(parsed);
    }
  }, [body]);

  const saveTags = async () => {
    await dal.cloudAccounts.restore.tags.set(props.account.id, updatedTags);
    await props.onUpdate();
  };

  const tagsChanged =
    JSON.stringify(originalTags) !== JSON.stringify(updatedTags);

  return (
    <PanelWrapper
      testIdPrefix='restore-account-config'
      header={{
        title: 'Edit restore server tags',
        subtitle: (
          <Stack className='px-[20px]' gap='24px'>
            <CloudProviderIndicator
              cloudProvider={props.account.cloudProvider}
              text={props.account.providerAccountId}
            />
            <Typography className='max-w-[680px]' variant='body1'>
              Restore operations run on a temporary server. To help identify it,
              you can add custom tags in addition to the default tags applied.
            </Typography>
          </Stack>
        ),
        onBack: props.onAbort,
        onClose: props.onAbort,
      }}
      footer={{
        primaryButton: {
          text: 'Save changes',
          onClick: saveTags,
          disabled: !tagsChanged,
          'data-testid': 'restore-config-tags-save-btn',
        },
        secondaryButton: {
          text: 'Cancel',
          onClick: props.onAbort,
          'data-testid': 'restore-config-tags-cancel-btn',
        },
      }}
    >
      <RestoreAccountConfigCustomTags
        tags={updatedTags}
        onChange={setUpdatedTags}
        isLoading={isLoading}
      />
    </PanelWrapper>
  );
}
